import AddIcon from '@material-ui/icons/Add';
import { useMemo } from 'react';
import { useContext } from 'react';
import { MentorId } from '../api/mentors';
import { Listing } from '../components/Pages/Listing';
import { MentorManagementRequestButton } from '../components/common';
import { MentorManagementRequestModal } from '../components/common/mentor-management-request-button';
import { OptionProps } from '../components/common/select';
import { WriteReviewModal } from '../components/common/write-review';
import BaseLayout from '../components/layout/base-layout';
import MyMentorsList from '../components/lists/my-mentors-list';
import MyMentorsTable from '../components/tables/my-mentors-table';
import {
  MyMentorsProvider,
  useMyMentorsContext,
} from '../contexts/my-mentors-context';
import { useResourceBundles } from '../contexts/resource-bundles-context';
import { UserContext } from '../contexts/user-context';
import { ProtectedRouteProps } from '../router/type';
import { isMobile } from '../utils/functions';

function MyMentors({ user }: ProtectedRouteProps) {
  const { hasAccessToAction, identityid } = useContext(UserContext);
  const { rb } = useResourceBundles();
  const {
    mentors,
    ventureOptions,
    selectedVenture,
    writeReviewTargetMentor,
    manageMentorTargetMentor,
    setWriteReviewTargetMentor,
    setManageMentorTargetMentor,
  } = useMyMentorsContext();

  const mentorOptions = useMemo(() => {
    const loadedMentorOptions: OptionProps<MentorId>[] = mentors.map((v) => ({
      value: v.id,
      label: `${v.firstName} ${v.lastName}`,
    }));
    return loadedMentorOptions;
  }, [mentors]);

  return (
    <BaseLayout user={user} fullWidth hiddenFooter={isMobile()}>
      <Listing
        title={`My ${rb('mentors-u')}`}
        actions={[
          {
            key: 'manage-mentors',
            label: `Manage ${rb('mentors-u')}`,
            hidden: !hasAccessToAction('mentor.details.manageMentor'),
            component: (
              <MentorManagementRequestButton
                title={`Manage ${rb('mentors-u')}`}
                mentorId={null}
                buttonText={`Manage ${rb('mentors-u')}`}
                defaultType='REMOVE'
                selectableType
                ventureId={selectedVenture !== 'ALL' ? selectedVenture : null}
                mentorOptions={mentorOptions}
                disabledButton={!mentorOptions?.length}
              />
            ),
          },
          {
            key: 'add-mentor',
            label: `Add ${rb('mentor-u')}`,
            hidden: !hasAccessToAction('mentor.mentorRequest'),
            component: (
              <MentorManagementRequestButton
                title={`Add ${rb('mentor-u')}`}
                caption={`Send a request to your programm admin to add a ${rb(
                  'mentor',
                )}`}
                ventureId={selectedVenture !== 'ALL' ? selectedVenture : null}
                buttonText={rb('mentor-u')}
                defaultType='ADD'
                startIcon={<AddIcon />}
                mentorId={null}
                ventureOptions={ventureOptions}
                disabledButton={!ventureOptions?.length}
                testid='add-mentor-management-button'
              />
            ),
          },
        ]}>
        {isMobile() ? <MyMentorsList /> : <MyMentorsTable />}
        {!!selectedVenture && !!writeReviewTargetMentor && (
          <WriteReviewModal
            open={!!writeReviewTargetMentor}
            onClose={() => setWriteReviewTargetMentor(null)}
            ventureId={selectedVenture}
            mentorName={writeReviewTargetMentor?.fullName}
            mentorId={writeReviewTargetMentor?.id}
          />
        )}
        {!!manageMentorTargetMentor && (
          <MentorManagementRequestModal
            title={`Manage ${manageMentorTargetMentor?.fullName}`}
            mentorId={manageMentorTargetMentor?.id || null}
            ventureId={selectedVenture}
            founderId={identityid}
            defaultType='REMOVE'
            selectableType
            open={!!manageMentorTargetMentor}
            onClose={() => setManageMentorTargetMentor(null)}
          />
        )}
      </Listing>
    </BaseLayout>
  );
}

function MyMentorsPage(props: ProtectedRouteProps) {
  return (
    <MyMentorsProvider>
      <MyMentors {...props} />
    </MyMentorsProvider>
  );
}

export default MyMentorsPage;
